import type { HierarchyConfig, HierarchyNode } from "./hierarchyGraph";
export type HierarchyRows = Record<string, Record<string, unknown>[]>;
export interface HierarchyExpansionRequest {
  alias: string;
  field: string;
  values: (string | number)[];
  page: number;
  follow?: { alias: string; field: string; linkField: string };
}
const valid = (value: unknown): value is string | number =>
  (typeof value === "string" && value.trim().length > 0) ||
  (typeof value === "number" && Number.isSafeInteger(value));
/** Plans only normal View queries. No metadata or bypass of View scope is needed at runtime. */
export function hierarchyExpansionRequests(
  config: HierarchyConfig,
  node: HierarchyNode,
  rows: HierarchyRows,
): HierarchyExpansionRequest[] {
  const tableId = JSON.parse(node.key)[0];
  const aliases = new Set(
    config.nodes
      .filter((mapping) => mapping.table_id === tableId)
      .map((mapping) => mapping.data_source_alias),
  );
  const result: HierarchyExpansionRequest[] = [];
  for (const relation of config.relationships) {
    const from = config.nodes.find(
      (mapping) =>
        mapping.data_source_alias === relation.from_data_source_alias,
    )!;
    const to = config.nodes.find(
      (mapping) => mapping.data_source_alias === relation.to_data_source_alias,
    )!;
    const source = relation.reverse ? to : from;
    const target = relation.reverse ? from : to;
    if (!aliases.has(source.data_source_alias)) continue;
    const id = JSON.parse(node.key)[1];
    const sourceRow = (rows[source.data_source_alias] || []).find(
      (row) => String(row[source.id_field]) === String(id),
    );
    const sourceId = sourceRow?.[source.id_field];
    if (!valid(sourceId)) continue;
    if (relation.link_data_source_alias) {
      result.push({
        alias: relation.link_data_source_alias,
        field: relation.reverse ? relation.to_field : relation.from_field,
        values: [sourceId],
        page: 1,
        follow: {
          alias: target.data_source_alias,
          field: target.id_field,
          linkField: relation.reverse ? relation.from_field : relation.to_field,
        },
      });
    } else if (relation.reverse) {
      result.push({
        alias: from.data_source_alias,
        field: relation.from_field,
        values: [sourceId],
        page: 1,
      });
    } else {
      const fk = sourceRow?.[relation.from_field];
      if (valid(fk))
        result.push({
          alias: to.data_source_alias,
          field: relation.to_field,
          values: [fk],
          page: 1,
        });
    }
  }
  return result;
}

/** Bounded page loading; unfinished filtered queries are returned for an explicit continuation. */
export async function fetchHierarchyExpansion(
  requests: HierarchyExpansionRequest[],
  query: (
    alias: string,
    request: HierarchyExpansionRequest,
  ) => Promise<{ rows: Record<string, unknown>[]; totalCount: number }>,
  maxRequests = 10,
  signal?: AbortSignal,
): Promise<{ rows: HierarchyRows; remaining: HierarchyExpansionRequest[] }> {
  const queue = [...requests];
  const rows: HierarchyRows = {};
  let count = 0;
  while (queue.length && count < maxRequests) {
    if (signal?.aborted)
      throw new DOMException("Hierarchy scope changed", "AbortError");
    const request = queue.shift()!;
    const response = await query(request.alias, request);
    if (signal?.aborted)
      throw new DOMException("Hierarchy scope changed", "AbortError");
    rows[request.alias] = [...(rows[request.alias] || []), ...response.rows];
    count++;
    if (request.page * 100 < response.totalCount) {
      if (!response.rows.length)
        throw new Error(
          "Hierarchy paging returned no rows before the reported total.",
        );
      queue.push({ ...request, page: request.page + 1 });
    }
    if (request.follow) {
      const values = [
        ...new Set(
          response.rows
            .map((row) => row[request.follow!.linkField])
            .filter(valid),
        ),
      ];
      for (let offset = 0; offset < values.length; offset += 100)
        queue.push({
          alias: request.follow.alias,
          field: request.follow.field,
          values: values.slice(offset, offset + 100),
          page: 1,
        });
    }
  }
  return { rows, remaining: queue };
}
